import React, { useCallback, useEffect, useMemo, useState } from "react";
import NotificationsToggle from "../components/NotificationsToggle";
import { GuideLeadsPanel } from "../components/GuideLeadsPanel";
import { QualityTab } from "../components/dash/QualityTab";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";
import {
  RefreshCw,
  LogOut,
  Phone,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Search,
  Bot,
  Globe,
  ChevronLeft,
  ChevronRight,
  Mail,
  PhoneCall,
  FileText,
} from "lucide-react";

const PIN_KEY = "dash_pin_v2";

// ─────────── Color system ───────────
// Sources (acquisition channels) — these are EXCLUSIVE: each form lead is attributed to
// exactly one source. Sum of all sources = leads.total. NO `site_form` here (legacy bastard).
const SRC = {
  google_ads: { color: "#f59e0b", label: "Google Ads", icon: Sparkles },
  seo:        { color: "#10b981", label: "SEO",        icon: Search },
  ai:         { color: "#a855f7", label: "AI / GPT",   icon: Bot },
  other:      { color: "#94a3b8", label: "Autres",     icon: Globe },
};
// Source keys = TRUE acquisition channels only (no site_form, which is a conversion mode)
const SRC_KEYS = ["google_ads", "seo", "ai", "other"];

// Sites palette (cool/distinct, doesn't clash with source colors)
const SITE_PALETTE = ["#06b6d4", "#6366f1", "#f43f5e", "#84cc16", "#ec4899"];

const GRID = "#1e293b";
const TXT = "#94a3b8";

// ─────────── helpers ───────────
const safe = (obj, ...path) => {
  let cur = obj;
  for (const p of path) {
    if (cur == null) return 0;
    cur = cur[p];
  }
  return Number(cur ?? 0);
};
const fmt = (n) => (typeof n === "number" ? n.toLocaleString("fr-FR") : n ?? "—");
const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);
const shortHost = (url) => {
  if (!url) return "";
  try { return new URL(url).hostname.replace(/^www\./, ""); }
  catch { return url; }
};
const monthLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};
const dayLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
};
const timeLabel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
};
const maskEmail = (email) => {
  if (!email || typeof email !== "string") return null;
  const [local, domain] = email.split("@");
  if (!domain) return email;
  if (local.length <= 2) return `${local[0] || "*"}***@${domain}`;
  return `${local.slice(0, 2)}***@${domain}`;
};
const maskPhone = (phone) => {
  if (!phone || typeof phone !== "string") return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 4) return phone;
  const last4 = digits.slice(-4);
  return `••• ${last4.slice(0, 2)} ${last4.slice(2)}`;
};
const FORM_TYPE_CFG = {
  peb:   { label: "PEB",   color: "#06b6d4" },
  audit: { label: "Audit", color: "#a855f7" },
  other: { label: "Autre", color: "#94a3b8" },
};
// Compact source badge for the leads list (subset of SRC, with short labels).
const SOURCE_BADGE = {
  google_ads: { label: "ADS", color: "#f59e0b" },
  seo:        { label: "SEO", color: "#10b981" },
  ai:         { label: "AI",  color: "#a855f7" },
  // "other" → no badge (avoids visual clutter)
};
const shortenLandingPath = (url) => {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url, "https://x.test");
    let p = (u.pathname || "/").replace(/\/+$/, "") || "/";
    if (p.length > 60) p = p.slice(0, 57) + "…";
    return p;
  } catch {
    return url.length > 60 ? url.slice(0, 57) + "…" : url;
  }
};
const ACTION_CFG = {
  form:         { label: "Formulaire", icon: FileText,  color: "#3b82f6" },
  phone_click:  { label: "Clic tél.",  icon: PhoneCall, color: "#10b981" },
  call:         { label: "Appel",      icon: PhoneCall, color: "#10b981" },
  email_click:  { label: "Email",      icon: Mail,      color: "#a855f7" },
};
const actionCfg = (a) =>
  ACTION_CFG[a] || { label: a || "Action", icon: Globe, color: "#94a3b8" };

// ─────────── PIN gate ───────────
const PinGate = ({ onSubmit, error, loading }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-4">
    <form
      data-testid="dash-pin-form"
      onSubmit={(e) => { e.preventDefault(); onSubmit(e.target.pin.value); }}
      className="w-full max-w-sm bg-slate-900/70 backdrop-blur-xl border border-slate-800 rounded-3xl p-7 shadow-2xl"
    >
      <div className="flex flex-col items-center mb-6">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 shadow-lg shadow-amber-500/30">
          <Sparkles className="w-7 h-7 text-slate-900" />
        </div>
        <h2 className="text-xl font-bold text-white">Google Ads Pilote</h2>
        <p className="text-slate-400 text-sm mt-1">PEB Network</p>
      </div>
      <input
        name="pin" type="password" placeholder="PIN" autoFocus inputMode="numeric"
        data-testid="dash-pin-input" disabled={loading}
        className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-700 text-white text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-amber-400/40"
      />
      <button
        type="submit" disabled={loading} data-testid="dash-pin-submit"
        className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-bold hover:shadow-lg hover:shadow-amber-500/30 transition-shadow disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        Déverrouiller
      </button>
      {error && (
        <div data-testid="dash-pin-error" className="text-red-400 text-sm text-center mt-4">
          {error}
        </div>
      )}
    </form>
  </div>
);

// ─────────── Tooltip ───────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900/95 border border-slate-700 rounded-lg px-3 py-2 shadow-xl">
      {label && <div className="text-xs font-semibold text-slate-300 mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs whitespace-nowrap">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-400">{p.name}:</span>
          <span className="text-white font-semibold">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ─────────── KPI card ───────────
const KpiCard = ({ label, value, sub, accent, icon: Icon }) => (
  <div className="relative bg-slate-900/60 backdrop-blur border border-slate-800 rounded-2xl p-4 sm:p-5 overflow-hidden">
    <div
      className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-10 blur-2xl"
      style={{ background: accent }}
    />
    <div className="flex items-start justify-between mb-1.5 relative">
      <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </div>
      {Icon && (
        <div className="w-7 h-7 rounded-lg flex items-center justify-center"
             style={{ background: `${accent}1A`, color: accent }}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      )}
    </div>
    <div className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight" style={{ color: accent }}>
      {fmt(value)}
    </div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

// ─────────── Empty state ───────────
const EmptyState = ({ msg, sub }) => (
  <div className="h-[260px] flex flex-col items-center justify-center text-slate-500 text-sm gap-2 px-4 text-center">
    <AlertTriangle className="w-7 h-7 opacity-40" />
    <div>{msg}</div>
    {sub && <div className="text-xs text-slate-600 max-w-md">{sub}</div>}
  </div>
);

// ─────────── Site card ───────────
const SiteCard = ({ raw, accent }) => {
  const offline = !!raw.error;
  const total = safe(raw, "leads", "total", "month");
  const ads = safe(raw, "leads", "google_ads", "month");
  const seo = safe(raw, "leads", "seo", "month");
  const ai = safe(raw, "leads", "ai", "month");
  const phone = safe(raw, "phone_clicks", "month");

  return (
    <div className="bg-slate-900/60 backdrop-blur border border-slate-800 rounded-2xl p-4 sm:p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: accent }} />
            <span className="text-white font-bold text-sm sm:text-base truncate">
              {raw.site_name || raw.site_id}
            </span>
          </div>
          {raw.site_url && (
            <a href={raw.site_url} target="_blank" rel="noopener noreferrer"
               className="text-xs text-slate-500 hover:text-slate-300 transition-colors block truncate">
              {shortHost(raw.site_url)}
            </a>
          )}
        </div>
        {offline ? (
          <span className="shrink-0 inline-flex items-center gap-1 bg-red-500/10 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
            <AlertTriangle className="w-3 h-3" />Offline
          </span>
        ) : (
          <span className="shrink-0 inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
            <CheckCircle2 className="w-3 h-3" />Online
          </span>
        )}
      </div>

      {!offline ? (
        <>
          <div className="flex items-end justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Leads (mois)</div>
              <div className="text-2xl sm:text-3xl font-extrabold text-white">{fmt(total)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Tél (mois)</div>
              <div className="text-2xl sm:text-3xl font-extrabold text-emerald-400">{fmt(phone)}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-800">
            <SourcePill val={ads} total={total} cfg={SRC.google_ads} />
            <SourcePill val={seo} total={total} cfg={SRC.seo} />
            <SourcePill val={ai} total={total} cfg={SRC.ai} />
          </div>
        </>
      ) : (
        <div className="text-xs text-slate-500 leading-relaxed break-all">{raw.error}</div>
      )}
    </div>
  );
};
const SourcePill = ({ val, total, cfg }) => {
  const Icon = cfg.icon;
  return (
    <div>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider" style={{ color: cfg.color }}>
        <Icon className="w-3 h-3" />{cfg.label}
      </div>
      <div className="text-sm font-bold text-white mt-0.5">{fmt(val)}</div>
      <div className="text-[10px] text-slate-500">{pct(val, total)}%</div>
    </div>
  );
};

// ─────────── Month selector ───────────
const MonthSelector = ({ months, selected, onChange }) => {
  const idx = months.indexOf(selected);
  const prev = () => idx > 0 && onChange(months[idx - 1]);
  const next = () => idx < months.length - 1 && onChange(months[idx + 1]);
  return (
    <div className="flex items-center gap-1 bg-slate-900/60 border border-slate-800 rounded-xl p-1">
      <button
        data-testid="dash-month-prev" onClick={prev} disabled={idx <= 0}
        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <select
        data-testid="dash-month-select" value={selected || ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-white text-sm font-semibold px-2 py-1 focus:outline-none cursor-pointer min-w-[140px]"
      >
        {months.map((m) => (
          <option key={m} value={m} className="bg-slate-900 text-white">
            {monthLabel(m)}
          </option>
        ))}
      </select>
      <button
        data-testid="dash-month-next" onClick={next} disabled={idx >= months.length - 1}
        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// ─────────── Hourly chart (24 hours of today, leads vs calls) ───────────
const HourlyChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <EmptyState msg="Données horaires non disponibles"
              sub={'Les sites doivent exposer "events[]" (contrat v2) pour afficher la répartition par heure.'} />;
  }
  const formatted = data.map((d) => ({
    h: `${String(d.hour).padStart(2, "0")}h`,
    leads: d.total ?? 0,
    phone: d.phone_clicks ?? 0,
  }));
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  // 24 hours wide on mobile → enable horizontal scroll
  const minWidth = isMobile ? 24 * 36 : "100%";
  return (
    <div className="overflow-x-auto -mx-1 px-1 pb-2" style={{ WebkitOverflowScrolling: "touch" }}>
      <div style={{ minWidth, height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={formatted} margin={{ top: 10, right: 10, left: -15, bottom: 0 }} barCategoryGap="15%">
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="h" tick={{ fill: TXT, fontSize: 10 }} tickLine={false} axisLine={{ stroke: GRID }}
                   interval={isMobile ? 1 : 0} />
            <YAxis tick={{ fill: TXT, fontSize: 10 }} tickLine={false} axisLine={{ stroke: GRID }} allowDecimals={false} />
            <Tooltip cursor={{ fill: "rgba(148,163,184,0.05)" }} content={<ChartTooltip />} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 11, color: TXT, paddingTop: 4 }} />
            <Bar dataKey="leads" name="Leads"      fill="#a855f7" radius={[3, 3, 0, 0]} />
            <Bar dataKey="phone" name="Clics tél." fill="#10b981" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ─────────── Daily chart (14 days, grouped by site) ───────────
const DailyChart = ({ data, sites, dataKey, label, palette }) => {
  if (!data || data.length === 0) {
    return <EmptyState msg="Données journalières non disponibles" sub={`Les sites doivent exposer le tableau "daily" pour afficher les ${label.toLowerCase()} sur 14 jours.`} />;
  }
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const minWidth = Math.max(640, data.length * 60);
  return (
    <div className="overflow-x-auto -mx-1 px-1 pb-2" style={{ WebkitOverflowScrolling: "touch" }}>
      <div style={{ minWidth: isMobile ? minWidth : "100%", height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: -15, bottom: 0 }} barCategoryGap="20%">
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="day" tick={{ fill: TXT, fontSize: 10 }} tickLine={false} axisLine={{ stroke: GRID }} interval={0} />
            <YAxis tick={{ fill: TXT, fontSize: 10 }} tickLine={false} axisLine={{ stroke: GRID }} allowDecimals={false} />
            <Tooltip cursor={{ fill: "rgba(148,163,184,0.05)" }} content={<ChartTooltip />} />
            {sites.map((s, i) => (
              <Bar
                key={s.idx}
                dataKey={`${s.idx}_${dataKey}`}
                name={s.host}
                fill={palette[s.idx % palette.length]}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ─────────── Monthly chart (per site, leads + calls grouped) ───────────
const MonthlyChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <EmptyState msg="Données mensuelles non disponibles" sub={'Les sites doivent exposer le tableau "monthly" pour afficher l\'historique par mois.'} />;
  }
  return (
    <div style={{ height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }} barCategoryGap="25%">
          <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="host" tick={{ fill: TXT, fontSize: 11 }} tickLine={false} axisLine={{ stroke: GRID }} />
          <YAxis tick={{ fill: TXT, fontSize: 11 }} tickLine={false} axisLine={{ stroke: GRID }} allowDecimals={false} />
          <Tooltip cursor={{ fill: "rgba(148,163,184,0.05)" }} content={<ChartTooltip />} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 11, color: TXT, paddingTop: 8 }} />
          <Bar dataKey="leads" name="Leads" fill="#a855f7" radius={[6, 6, 0, 0]} />
          <Bar dataKey="phone" name="Clics tél." fill="#10b981" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─────────── Sources donut ───────────
const SourcesDonut = ({ total }) => {
  const data = SRC_KEYS.filter((k) => total[k] > 0).map((k) => ({
    name: SRC[k].label, value: total[k], fill: SRC[k].color,
  }));
  if (data.length === 0) return <EmptyState msg="Pas de leads ce mois" />;
  const sum = data.reduce((a, b) => a + b.value, 0);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center">
      <div style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={50} outerRadius={75} paddingAngle={3} stroke="none">
              {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-1.5 text-xs">
        {data.map((d) => (
          <div key={d.name} className="flex items-center gap-2 whitespace-nowrap">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.fill }} />
            <span className="text-slate-300 font-medium w-20">{d.name}</span>
            <span className="text-white font-bold tabular-nums">{fmt(d.value)}</span>
            <span className="text-slate-500 tabular-nums">({pct(d.value, sum)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────── Contact channel (form vs phone) ───────────
const ContactChannelBlock = ({ form, phone }) => {
  const total = form + phone;
  if (total === 0) return <EmptyState msg="Pas de contacts ce mois" />;
  const formPct = pct(form, total);
  const phonePct = 100 - formPct;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-blue-400 mb-1">
            <FileText className="w-3 h-3" />
            Formulaire
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-white tabular-nums">{fmt(form)}</div>
          <div className="text-[11px] text-slate-500">{formPct}% des contacts</div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-400 mb-1 justify-end">
            <PhoneCall className="w-3 h-3" />
            Téléphone
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-emerald-400 tabular-nums">{fmt(phone)}</div>
          <div className="text-[11px] text-slate-500">{phonePct}% des contacts</div>
        </div>
      </div>
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-950/60 border border-slate-800/60">
        {form > 0 && (
          <div
            className="bg-blue-500"
            style={{ width: `${formPct}%` }}
            title={`Formulaire: ${form}`}
          />
        )}
        {phone > 0 && (
          <div
            className="bg-emerald-500"
            style={{ width: `${phonePct}%` }}
            title={`Téléphone: ${phone}`}
          />
        )}
      </div>
      <div className="text-[11px] text-slate-500 leading-relaxed">
        Les sources Google Ads / SEO / AI / Autres ci-contre indiquent <em>d'où</em> viennent les leads ;
        ce bloc indique <em>comment</em> ils ont contacté.
      </div>
    </div>
  );
};

// ─────────── Today's raw leads list ───────────
const TodayLeadsList = ({ leads, sitesByHost }) => {
  if (!leads || leads.length === 0) return null;
  return (
    <div className="mt-4 pt-4 border-t border-amber-400/10" data-testid="dash-today-leads">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-[11px] sm:text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2">
          <Mail className="w-3.5 h-3.5 text-amber-300" />
          Détail des leads du jour
        </h3>
        <span className="text-[10px] sm:text-xs text-slate-500 tabular-nums">
          {leads.length} entrée{leads.length > 1 ? "s" : ""}
        </span>
      </div>
      <div
        className="bg-slate-950/40 border border-slate-800/60 rounded-xl divide-y divide-slate-800/60 max-h-[420px] overflow-y-auto"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {leads.map((l, i) => {
          const cfg = actionCfg(l.action);
          const Icon = cfg.icon;
          const masked = maskEmail(l.email);
          const phoneMasked = maskPhone(l.phone);
          const siteColor = sitesByHost[l.site_host]?.color || "#94a3b8";
          // Pick the most relevant identifier to show as the main "contact" cell
          const contact = masked || phoneMasked;
          const formTypeCfg = FORM_TYPE_CFG[l.form_type] || FORM_TYPE_CFG.other;
          const srcBadge = SOURCE_BADGE[l.source];
          // Suspicious attribution: source=seo but a keyword is set.
          // Organic Google traffic doesn't expose the search query (not_provided since 2011);
          // a keyword on an "seo" lead almost always comes from utm_term → likely a misattributed Ads click
          // whose gclid was stripped (redirect, ad-blocker, etc.).
          const suspicious = l.source === "seo" && !!l.keyword;
          const landingPath = shortenLandingPath(l.landing_url);
          const showLanding = !!landingPath;
          const hasSecondLine = (masked && phoneMasked) || l.keyword || showLanding;
          return (
            <div
              key={`${l.timestamp}-${i}`}
              className="px-3 py-2.5 hover:bg-slate-900/40 transition-colors"
              data-testid={`dash-today-lead-${i}`}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <span
                  className="text-[10px] sm:text-xs font-mono tabular-nums text-slate-500 shrink-0 w-10 sm:w-12"
                  title={l.timestamp || ""}
                >
                  {timeLabel(l.timestamp)}
                </span>
                <span
                  className="hidden sm:inline-flex shrink-0 w-1.5 h-6 rounded-full"
                  style={{ background: siteColor }}
                />
                <span className="text-[10px] sm:text-xs text-slate-300 font-medium truncate w-20 sm:w-32 shrink-0">
                  {l.site_host || l.site_id}
                </span>
                <span
                  className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold shrink-0 px-1.5 py-0.5 rounded"
                  style={{ color: cfg.color, background: `${cfg.color}1A` }}
                >
                  <Icon className="w-3 h-3" />
                  <span className="hidden sm:inline">{cfg.label}</span>
                </span>
                {l.form_type && l.form_type !== "other" && (
                  <span
                    className="inline-flex items-center text-[10px] font-bold shrink-0 px-1.5 py-0.5 rounded uppercase tracking-wider"
                    style={{ color: formTypeCfg.color, background: `${formTypeCfg.color}1A` }}
                    title={`Formulaire ${formTypeCfg.label}`}
                  >
                    {formTypeCfg.label}
                  </span>
                )}
                {srcBadge && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-bold shrink-0 px-1.5 py-0.5 rounded uppercase tracking-wider"
                    style={{ color: srcBadge.color, background: `${srcBadge.color}1A` }}
                    title={`Source : ${srcBadge.label}`}
                  >
                    {srcBadge.label}
                    {suspicious && (
                      <AlertTriangle
                        className="w-3 h-3 text-amber-400"
                        title="Attribution suspecte : un keyword est présent sur un lead SEO. Vient probablement d'un clic Google Ads dont le gclid a été perdu."
                      />
                    )}
                  </span>
                )}
                <span className="text-[10px] sm:text-xs text-slate-200 font-mono truncate flex-1 min-w-0">
                  {contact || <span className="text-slate-600 italic">—</span>}
                </span>
              </div>
              {hasSecondLine && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 ml-12 sm:ml-[5.5rem] text-[10px] sm:text-[11px]">
                  {masked && phoneMasked && (
                    <span className="inline-flex items-center gap-1 text-emerald-300/80 font-mono">
                      <PhoneCall className="w-3 h-3" />
                      {phoneMasked}
                    </span>
                  )}
                  {l.keyword && (
                    <span
                      className="inline-flex items-center gap-1 text-slate-400 truncate max-w-[60%]"
                      title={l.keyword}
                    >
                      <Search className="w-3 h-3 text-amber-400/70 shrink-0" />
                      <span className="font-medium text-slate-300 truncate">{l.keyword}</span>
                    </span>
                  )}
                  {showLanding && (
                    <a
                      href={l.landing_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-slate-400 hover:text-emerald-300 transition-colors truncate max-w-[80%]"
                      title={l.landing_url}
                    >
                      <Globe className="w-3 h-3 text-emerald-400/70 shrink-0" />
                      <span className="font-medium text-slate-300 truncate">{landingPath}</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────── Main ───────────
const DashPage = () => {
  const [pin, setPin] = useState(() => localStorage.getItem(PIN_KEY) || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [tab, setTab] = useState("traffic");

  // noindex
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Dashboard";
    let robotsMeta = document.querySelector('meta[name="robots"]');
    const previousRobots = robotsMeta ? robotsMeta.getAttribute("content") : null;
    const created = !robotsMeta;
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", "noindex, nofollow, noarchive, nosnippet");
    const ref = document.createElement("meta");
    ref.name = "referrer"; ref.content = "no-referrer";
    document.head.appendChild(ref);
    return () => {
      document.title = prevTitle;
      if (created) robotsMeta.remove();
      else if (previousRobots !== null) robotsMeta.setAttribute("content", previousRobots);
      document.head.removeChild(ref);
    };
  }, []);

  const fetchStats = useCallback(async () => {
    if (!pin) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/dash/stats?pin=${encodeURIComponent(pin)}`
      );
      if (r.status === 403) {
        setError("PIN incorrect");
        localStorage.removeItem(PIN_KEY);
        setPin("");
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setData(json);
      setError("");
    } catch (e) {
      setError(`Erreur réseau: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [pin]);

  // Load on mount + when pin set, only ONCE per page load (no auto-refresh interval)
  useEffect(() => {
    if (pin) {
      localStorage.setItem(PIN_KEY, pin);
      fetchStats();
    }
  }, [pin, fetchStats]);

  const handleLogout = () => {
    localStorage.removeItem(PIN_KEY);
    setPin("");
    setData(null);
    setError("");
  };

  // ───────── derived ─────────
  const sites = useMemo(() => {
    if (!data) return [];
    return data.sites.map((s, i) => ({
      idx: i, raw: s, name: s.site_name || s.site_id,
      host: shortHost(s.site_url) || s.site_id,
      offline: !!s.error,
      color: SITE_PALETTE[i % SITE_PALETTE.length],
    }));
  }, [data]);
  const onlineSites = sites.filter((s) => !s.offline);

  // KPI totals (current month)
  // Aggregated month totals (current calendar month, all online sites).
  // Key invariant: total = sum of all sources = google_ads + seo + ai + other.
  // `phone` is the SEPARATE phone_clicks counter (different dimension).
  const monthTotals = useMemo(() => {
    if (!data) return null;
    const acc = { total: 0, google_ads: 0, seo: 0, ai: 0, other: 0, phone: 0 };
    onlineSites.forEach((s) => {
      acc.total      += safe(s.raw, "leads", "total", "month");
      acc.google_ads += safe(s.raw, "leads", "google_ads", "month");
      acc.seo        += safe(s.raw, "leads", "seo", "month");
      acc.ai         += safe(s.raw, "leads", "ai", "month");
      acc.other      += safe(s.raw, "leads", "other", "month");
      acc.phone      += safe(s.raw, "phone_clicks", "month");
    });
    return acc;
  }, [data, onlineSites]);

  // Today totals + per-site breakdown
  const todayBySite = useMemo(() => {
    return onlineSites.map((s) => ({
      idx: s.idx,
      host: s.host,
      color: s.color,
      leads: safe(s.raw, "leads", "total", "today"),
      phone: safe(s.raw, "phone_clicks", "today"),
    }));
  }, [onlineSites]);
  const todayTotals = useMemo(() => {
    return todayBySite.reduce(
      (acc, s) => ({ leads: acc.leads + s.leads, phone: acc.phone + s.phone }),
      { leads: 0, phone: 0 }
    );
  }, [todayBySite]);

  // 14-day daily series — merge all sites by date
  const dailySeries = useMemo(() => {
    if (!onlineSites.length) return [];
    const dateSet = new Set();
    onlineSites.forEach((s) => {
      (s.raw.daily || []).forEach((d) => dateSet.add(d.date));
    });
    const dates = [...dateSet].sort().slice(-14);
    return dates.map((date) => {
      const row = { date, day: dayLabel(date) };
      onlineSites.forEach((s) => {
        const d = (s.raw.daily || []).find((x) => x.date === date) || {};
        row[`${s.idx}_total`] = Number(d.total ?? 0);
        row[`${s.idx}_phone`] = Number(d.phone_clicks ?? 0);
      });
      return row;
    });
  }, [onlineSites]);

  // Available months across all sites
  const availableMonths = useMemo(() => {
    const set = new Set();
    onlineSites.forEach((s) => (s.raw.monthly || []).forEach((m) => set.add(m.month)));
    return [...set].sort();
  }, [onlineSites]);

  // Initialize selected month to most recent available
  useEffect(() => {
    if (!selectedMonth && availableMonths.length) {
      setSelectedMonth(availableMonths[availableMonths.length - 1]);
    }
  }, [availableMonths, selectedMonth]);

  // Monthly view — leads + phone per site for selected month
  const monthlyPerSite = useMemo(() => {
    if (!selectedMonth) return [];
    return onlineSites.map((s) => {
      const m = (s.raw.monthly || []).find((x) => x.month === selectedMonth) || {};
      return {
        host: s.host, name: s.name, color: s.color,
        leads: Number(m.total ?? 0),
        phone: Number(m.phone_clicks ?? 0),
        google_ads: Number(m.google_ads ?? 0),
        seo: Number(m.seo ?? 0),
        ai: Number(m.ai ?? 0),
        other: Number(m.other ?? 0),
      };
    });
  }, [onlineSites, selectedMonth]);
  const monthlyTotals = useMemo(() => {
    const acc = { google_ads: 0, seo: 0, ai: 0, other: 0 };
    monthlyPerSite.forEach((s) => SRC_KEYS.forEach((k) => (acc[k] += s[k] || 0)));
    return acc;
  }, [monthlyPerSite]);

  // Hourly (today) — sum across all online sites. Uses v2 contract (events[]).
  const hourlyToday = useMemo(() => {
    if (!onlineSites.length) return [];
    // Detect if at least one site exposes hourly data
    const anyHourly = onlineSites.some((s) => Array.isArray(s.raw.hourly) && s.raw.hourly.length === 24);
    if (!anyHourly) return [];
    const merged = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      total: 0,
      phone_clicks: 0,
    }));
    onlineSites.forEach((s) => {
      const hr = s.raw.hourly;
      if (!Array.isArray(hr)) return;
      hr.forEach((row) => {
        const h = Number(row.hour);
        if (!Number.isInteger(h) || h < 0 || h > 23) return;
        merged[h].total       += Number(row.total ?? 0);
        merged[h].phone_clicks += Number(row.phone_clicks ?? 0);
      });
    });
    return merged;
  }, [onlineSites]);

  if (!pin || error === "PIN incorrect") {
    return (
      <PinGate
        onSubmit={(p) => { setError(""); setPin(p); }}
        error={error} loading={loading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100" data-testid="dash-page">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">

        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20 shrink-0">
              <Sparkles className="w-5 h-5 text-slate-900" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight truncate">
                Google Ads Pilote
              </h1>
              <p className="text-slate-400 text-xs sm:text-sm">
                PEB Network · {onlineSites.length}/{sites.length} sites en ligne
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-auto">
            <button
              data-testid="dash-refresh-button" onClick={fetchStats} disabled={loading}
              className="p-2.5 rounded-xl bg-slate-900/60 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-700 transition-colors disabled:opacity-50"
              title="Recharger">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid="dash-logout-button" onClick={handleLogout}
              className="p-2.5 rounded-xl bg-slate-900/60 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-700 transition-colors"
              title="Logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        <GuideLeadsPanel pin={pin} refreshKey={data} />

        <div className="flex gap-1 p-1 mb-6 rounded-xl bg-slate-900/60 border border-slate-800 w-fit" data-testid="dash-tabs">
          {[["traffic", "Trafic"], ["quality", "Qualité & Commandes"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`dash-tab-${k}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === k ? "bg-amber-400 text-slate-900" : "text-slate-400 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "quality" && <QualityTab pin={pin} refreshKey={data} />}

        {tab === "traffic" && error && !data && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-4 mb-5 text-sm" data-testid="dash-error">
            {error}
          </div>
        )}

        {tab === "traffic" && !data && !error && (
          <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Chargement…
          </div>
        )}

        {tab === "traffic" && data && monthTotals && (
          <>
            {/* TODAY hero — most prominent block */}
            <section
              className="mb-6 bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-rose-500/10 border border-amber-400/20 rounded-2xl p-4 sm:p-6 backdrop-blur"
              data-testid="dash-today"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400" />
                </span>
                <h2 className="text-sm sm:text-base font-bold uppercase tracking-wider text-amber-300">
                  Aujourd'hui
                </h2>
                <span className="text-[10px] sm:text-xs text-slate-500 ml-auto">
                  {new Date().toLocaleDateString("fr-FR", {
                    weekday: "long", day: "numeric", month: "long",
                  })}
                </span>
              </div>

              {/* Big totals */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
                <div className="bg-slate-950/40 border border-slate-800/60 rounded-xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <Globe className="w-4 h-4 text-blue-400" />
                    <span className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-400 font-semibold">
                      Leads / clicks
                    </span>
                  </div>
                  <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tabular-nums leading-none">
                    {fmt(todayTotals.leads)}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1.5">
                    sur {onlineSites.length} site{onlineSites.length > 1 ? "s" : ""}
                  </div>
                </div>
                <div className="bg-slate-950/40 border border-slate-800/60 rounded-xl p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <Phone className="w-4 h-4 text-emerald-400" />
                    <span className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-400 font-semibold">
                      Calls
                    </span>
                  </div>
                  <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-emerald-400 tabular-nums leading-none">
                    {fmt(todayTotals.phone)}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1.5">
                    clics téléphone
                  </div>
                </div>
              </div>

              {/* Per-site breakdown */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {todayBySite.map((s) => (
                  <div
                    key={s.idx}
                    className="bg-slate-950/40 border border-slate-800/60 rounded-xl p-3 flex items-center gap-3"
                    data-testid={`dash-today-site-${s.idx}`}
                  >
                    <span className="w-2 h-10 rounded-full shrink-0" style={{ background: s.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-slate-300 font-medium truncate">
                        {s.host}
                      </div>
                      <div className="flex items-baseline gap-3 mt-0.5">
                        <div className="flex items-baseline gap-1">
                          <span className="text-lg font-bold text-white tabular-nums">
                            {fmt(s.leads)}
                          </span>
                          <span className="text-[10px] text-slate-500">leads</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-lg font-bold text-emerald-400 tabular-nums">
                            {fmt(s.phone)}
                          </span>
                          <span className="text-[10px] text-slate-500">calls</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {todayTotals.leads === 0 && todayTotals.phone === 0 && (
                <div className="text-center text-xs text-slate-500 mt-3">
                  Aucune activité enregistrée aujourd'hui pour l'instant.
                </div>
              )}

              {/* Raw leads of the day (forms + phone clicks) */}
              <TodayLeadsList
                leads={data.today_leads || []}
                sitesByHost={Object.fromEntries(onlineSites.map((s) => [s.host, s]))}
              />
            </section>

            {/* KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="dash-totals">
              <KpiCard label="Leads ce mois" value={monthTotals.total}
                       sub={`${monthTotals.phone} clics tél.`} accent="#a855f7" icon={Globe} />
              <KpiCard label="Google Ads" value={monthTotals.google_ads}
                       sub={`${pct(monthTotals.google_ads, monthTotals.total)}% du total`}
                       accent={SRC.google_ads.color} icon={SRC.google_ads.icon} />
              <KpiCard label="SEO (organique)" value={monthTotals.seo}
                       sub={`${pct(monthTotals.seo, monthTotals.total)}% du total`}
                       accent={SRC.seo.color} icon={SRC.seo.icon} />
              <KpiCard label="AI / ChatGPT" value={monthTotals.ai}
                       sub={`${pct(monthTotals.ai, monthTotals.total)}% du total`}
                       accent={SRC.ai.color} icon={SRC.ai.icon} />
            </div>

            {/* Sources donut + contact channel side by side */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-900/60 backdrop-blur border border-slate-800 rounded-2xl p-4 sm:p-5">
                <h2 className="text-sm font-bold text-white mb-3">Acquisition ce mois — d'où viennent les leads</h2>
                <SourcesDonut total={monthTotals} />
              </div>
              <div className="bg-slate-900/60 backdrop-blur border border-slate-800 rounded-2xl p-4 sm:p-5">
                <h2 className="text-sm font-bold text-white mb-3">Mode de contact ce mois — comment ils contactent</h2>
                <ContactChannelBlock form={monthTotals.total} phone={monthTotals.phone} />
              </div>
            </section>

            {/* Hourly (today) + 14-day section */}
            <section className="mb-6">
              <h2 className="text-base sm:text-lg font-bold text-white mb-3 flex items-center gap-2">
                <span className="w-1 h-5 bg-amber-400 rounded-full" />
                Aujourd'hui — par heure
              </h2>
              <ChartBlock
                title={`Activité horaire — ${new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}`}
                subtitle="Tous sites confondus, 00h → 23h"
              >
                <HourlyChart data={hourlyToday} />
              </ChartBlock>
            </section>

            <section className="mb-6">
              <h2 className="text-base sm:text-lg font-bold text-white mb-3 flex items-center gap-2">
                <span className="w-1 h-5 bg-amber-400 rounded-full" />
                14 derniers jours
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ChartBlock title="Leads par jour" subtitle="3 barres = 3 sites">
                  <DailyChart data={dailySeries} sites={onlineSites} dataKey="total" label="leads" palette={SITE_PALETTE} />
                </ChartBlock>
                <ChartBlock title="Clics téléphone par jour" subtitle="3 barres = 3 sites">
                  <DailyChart data={dailySeries} sites={onlineSites} dataKey="phone" label="clics" palette={SITE_PALETTE} />
                </ChartBlock>
              </div>
              <SitesLegend sites={onlineSites} />
            </section>

            {/* Monthly section */}
            <section className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                  <span className="w-1 h-5 bg-purple-400 rounded-full" />
                  Vue mensuelle
                </h2>
                {availableMonths.length > 0 && (
                  <MonthSelector
                    months={availableMonths}
                    selected={selectedMonth || availableMonths[availableMonths.length - 1]}
                    onChange={setSelectedMonth}
                  />
                )}
              </div>
              <ChartBlock
                title={`Leads & clics tél. par site — ${monthLabel(selectedMonth)}`}
                subtitle="Double barre par site (leads / appels)"
              >
                <MonthlyChart data={monthlyPerSite} />
              </ChartBlock>

              {/* Source breakdown for selected month */}
              {monthlyPerSite.some((s) => s.leads > 0) && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {SRC_KEYS.map((k) => (
                    <div key={k} className="bg-slate-900/60 border border-slate-800 rounded-xl p-3">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1" style={{ color: SRC[k].color }}>
                        {React.createElement(SRC[k].icon, { className: "w-3 h-3" })}
                        {SRC[k].label}
                      </div>
                      <div className="text-lg font-bold text-white">{fmt(monthlyTotals[k])}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Per-site cards */}
            <section className="mb-6">
              <h2 className="text-base sm:text-lg font-bold text-white mb-3 flex items-center gap-2">
                <span className="w-1 h-5 bg-cyan-400 rounded-full" />
                État des sites
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="dash-sites">
                {sites.map((s) => <SiteCard key={s.idx} raw={s.raw} accent={s.color} />)}
              </div>
            </section>

            {/* Push notifications toggle (moved to bottom, not critical UX) */}
            <NotificationsToggle pin={pin} />

            <footer className="text-center text-xs text-slate-600 pt-4" data-testid="dash-footer">
              {loading ? "Chargement…" : `Mis à jour : ${new Date(data.fetched_at).toLocaleString("fr")}`}
              {" "}· Recharge la page ou clique <RefreshCw className="inline w-3 h-3 -mt-0.5" /> pour mettre à jour
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────── Sub-components ───────────
const ChartBlock = ({ title, subtitle, children }) => (
  <div className="bg-slate-900/60 backdrop-blur border border-slate-800 rounded-2xl p-4 sm:p-5">
    <div className="flex items-center justify-between mb-3">
      <div>
        <h3 className="text-sm font-bold text-white">{title}</h3>
        {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const SitesLegend = ({ sites }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 px-2 text-[11px] sm:text-xs">
    {sites.map((s) => (
      <span key={s.idx} className="flex items-center gap-1.5 text-slate-400">
        <span className="w-3 h-3 rounded-sm" style={{ background: s.color }} />
        <span className="font-medium">{s.host}</span>
      </span>
    ))}
  </div>
);

export default DashPage;
